/**
 * `PublicCvDashboard` — vista read-only e pubblica della dashboard di un profilo
 * CV esterno, resa FUORI dal `Layout` (nessuna navigazione/chrome di piattaforma,
 * Req. 4.3, 6.1, 6.3).
 *
 * È un componente puramente presentazionale: riceve già i dati aggregati
 * ({@link PublicExternalCvDashboardDto}) come prop e non effettua alcuna fetch —
 * il fetching (e il polling dello stato AI) è responsabilità di `PublicCvRecap`,
 * che passa i dati a questo componente una volta `completed`.
 *
 * Il rendering è delegato al componente condiviso {@link ExternalCvProfileView},
 * lo stesso usato dalla pagina interna `ExternalCvDetail`:
 *  - i drill-down di competenza, professione e certificazione sono abilitati
 *    verso le rotte PUBBLICHE (`/public/submission/:token/...`), identificate dal
 *    Public_Token e non da un id interno, tramite i rispettivi `*DetailPath`;
 *  - nessuna azione di piattaforma (scarica/elimina/rielabora) né chrome
 *    autenticato;
 *  - il `profile` è {@link PublicExternalCvProfileSummary}, privo dell'`id`
 *    interno (Req. 2.2).
 * Includendo `parsedCv`/`parsedCvStatus` (ora presenti nel DTO pubblico) la vista
 * pubblica mostra anche tutte le sezioni del CV Europass.
 */
import {
  type ExternalCvAiStatus,
  type PublicExternalCvDashboardDto,
} from '../../shared/dto';
import { ExternalCvProfileView } from '../../components/ExternalCvProfileView';
import { Badge, PageHeader, type BadgeTone } from '../../components/ui';

/** Etichetta e tono della badge per ciascun esito della pre-compilazione AI. */
const AI_STATUS_META: Record<ExternalCvAiStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Pre-compilazione in corso', tone: 'warning' },
  completed: { label: 'Pre-compilazione completata', tone: 'success' },
  failed: { label: 'Pre-compilazione non riuscita', tone: 'danger' },
};

export default function PublicCvDashboard({
  data,
  token,
}: {
  data: PublicExternalCvDashboardDto;
  /** Public_Token del profilo: usato per il link al wizard di autovalutazione. */
  token: string;
}) {
  const { profile } = data;
  const status = AI_STATUS_META[profile.aiStatus];
  const fullName = `${profile.firstName} ${profile.lastName}`.trim();

  const header = (
    <PageHeader
      title={fullName}
      titleId="external-cv-detail-title"
      subtitle="Riepilogo del profilo derivato dalle valutazioni AI."
      titleAdornment={<Badge tone={status.tone}>{status.label}</Badge>}
    />
  );

  const encodedToken = encodeURIComponent(token);
  // Path pubblici dei dettagli, identificati dal Public_Token (non da un id
  // interno). Fornendo i rispettivi `*DetailPath`, le card competenza/professione
  // diventano navigabili e le card certificazione ottengono lo "stretched link",
  // esattamente come nel flusso interno (`ExternalCvDetail`), ma verso le rotte
  // pubbliche.
  const competenceDetailPath = (competenceId: string) =>
    `/public/submission/${encodedToken}/competences/${encodeURIComponent(competenceId)}`;
  const jobDetailPath = (jobId: string) =>
    `/public/submission/${encodedToken}/jobs/${encodeURIComponent(jobId)}`;
  const certificationDetailPath = (certificationId: string) =>
    `/public/submission/${encodedToken}/certifications/${encodeURIComponent(certificationId)}`;

  return (
    <ExternalCvProfileView
      header={header}
      dashboard={data.dashboard}
      assessments={data.assessments}
      jobPositioning={data.jobPositioning}
      certifications={data.certifications ?? []}
      parsedCv={data.parsedCv}
      parsedCvStatus={data.parsedCvStatus}
      competenceDetailPath={competenceDetailPath}
      jobDetailPath={jobDetailPath}
      certificationDetailPath={certificationDetailPath}
      evaluateHref={`/public/submission/${encodedToken}/valuta`}
    />
  );
}
